import { CurrencyPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injectable, NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  ActivatedRoute,
  NavigationEnd,
  PreloadingStrategy,
  Route,
  Router,
  RouterModule,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import { environment } from '@environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { FullRouterStateSerializer, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { Action, ActionReducer, MetaReducer, Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SharedToastComponent } from '@shared-lib/components/toast/shared-toast.component';
import { Environment } from '@shared-lib/environment/environment';
import { ErrorReasonInterceptor } from '@shared-lib/interceptors/error-reason.interceptor';
import { ApplicationHttpClient, applicationHttpClientCreator } from '@shared-lib/services/application-http.service';
import { SentryService } from '@shared-lib/services/sentry.service';
import { MobileAppVersionNotCompatibleComponent } from '@shared/components/mobile-app-version-not-compatible/mobile-app-version-not-compatible.component';
import { PreloadCheckpointsEnum } from '@shared/enums/preload-checkpoints.enum';
import { AuthGuard } from '@shared/guards/auth.guard';
import { AuthenticatedGuard } from '@shared/guards/authenticated.guard';
import { DashlyErrorInterceptor } from '@shared/interceptors/dashly-error-interceptor';
import { DashlyHttpInterceptor } from '@shared/interceptors/dashly-http-interceptor';
import { AppState } from '@shared/models/app-state.model';
import { AuthService } from '@shared/services/auth.service';
import { CookieConsentService } from '@shared/services/cookie-consent.service';
import { ErrorHandlerModule } from '@shared/services/error-handler/error-handler.module';
import { NavigationService } from '@shared/services/navigation.service';
import { effects, reducers } from '@shared/store';
import { logoutAndRedirect, resetStore } from '@shared/store/auth/auth.action';
import { CustomSerializer } from '@shared/store/route-serializer';
import { CookieModule } from 'ngx-cookie';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppComponent } from './app.component';
import { routes } from './app.routes';
import { HandleAppOpenComponent } from './landing/components/handle-app-open/handle-app-open.component';
import { handleDetailViewTransition } from './platform/detail-view-transition';

@Injectable({ providedIn: 'root' })
export class PreloadSelectedModulesList implements PreloadingStrategy {
  checkpoints: Set<PreloadCheckpointsEnum> = new Set<PreloadCheckpointsEnum>();
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            // gets the deepest child
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
      )
      .subscribe((route) => {
        if (route.snapshot.data['preloadCheckpoint'] != null) {
          this.checkpoints.add(route.snapshot.data['preloadCheckpoint']);
        }
      });
  }

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> | null {
    if (route.data && (route.data['preload'] || this.checkpoints.has(route.data['preloadAfterCheckpoint']))) {
      return load();
    } else {
      return of(null);
    }
  }
}

export const resetState = (reducer: ActionReducer<Partial<AppState>>): ActionReducer<Partial<AppState>> => {
  return (state: Partial<AppState>, action: Action): Partial<AppState> => {
    switch (action.type) {
      case logoutAndRedirect.type:
      case resetStore.type:
        return reducer({}, action);
      default:
        return reducer(state, action);
    }
  };
};

export const metaReducers: MetaReducer[] = [resetState];

@Injectable()
export class DefaultHammerConfig extends HammerGestureConfig {
  override overrides = {
    swipe: { direction: Hammer.DIRECTION_ALL },
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    EffectsModule.forRoot([...effects]),
    StoreDevtoolsModule.instrument({
      maxAge: 75,
    }),
    StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
    RouterModule,
    HammerModule,
    ErrorHandlerModule,
    GoogleMapsModule,
    SharedToastComponent,
    MobileAppVersionNotCompatibleComponent,
    CookieModule.withOptions(),
    ConfirmDialogModule,
    HandleAppOpenComponent,
  ],
  providers: [
    MessageService,
    CurrencyPipe,
    NavigationService,
    PreloadSelectedModulesList,
    GoogleMapsModule,
    DialogService,
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    provideAppInitializer(() => {
      const store = inject(Store);
      const authService = inject(AuthService);
      const cookieConsentService = inject(CookieConsentService);

      inject(SentryService).init();

      if (cookieConsentService.getCookieConsent() || environment.mobileApp?.isMobileApp) {
        cookieConsentService.loadAnalytics();
      }

      // if (
      //   (window.location.href.includes(paths.PLATFORM) || window.location.href.includes(paths.auth.TERMS_AND_CONDITIONS)) &&
      //   authService.token()
      // ) {
      //   store.dispatch(getClientUser());
      //   store.select(selectClientUser).pipe(
      //     filter(Boolean),
      //     take(1),
      //     map(() => true),
      //   );
      // }
    }),
    {
      provide: Environment,
      useValue: environment,
    },
    {
      provide: ApplicationHttpClient,
      useFactory: applicationHttpClientCreator,
      deps: [HttpClient, Environment],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DashlyErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DashlyHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorReasonInterceptor,
      multi: true,
    },
    AuthGuard,
    AuthenticatedGuard,
    // PrimeNG
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        options: {
          cssLayer: {
            name: 'primeng',
          },
        },
      },
    }),
    // Angular
    provideRouter(
      routes,
      withPreloading(PreloadSelectedModulesList),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withViewTransitions({ onViewTransitionCreated: handleDetailViewTransition }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HAMMER_GESTURE_CONFIG, useClass: DefaultHammerConfig },
  ],
})
export class AppModule {}
